/**
 * Partner requests management hook.
 *
 * Manages CRUD operations for user's partner requests. Handles fetching,
 * pausing/resuming, and deleting requests. Automatically sorts requests
 * by status (active first) and level.
 *
 * @module usePartnerRequests
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { PartnerRequest } from '@/types/partner-finder';
import {
  getMyPartnerRequests,
  deletePartnerRequest,
  togglePartnerRequestStatus,
} from '@/lib/partnerApi';
import { mapApiRequestToPartnerRequest } from '@/lib/mappers';
import { sortRequests } from '@/lib/partnerRequestUtils';
import { useAsyncData } from './useAsyncData';

interface UsePartnerRequestsReturn {
  readonly requests: PartnerRequest[];
  readonly isLoading: boolean;
  readonly isPausing: string | null;
  readonly requestToDelete: string | null;
  readonly setRequestToDelete: (id: string | null) => void;
  readonly handlePauseToggle: (requestId: string) => Promise<void>;
  readonly handleDelete: (requestId: string) => Promise<void>;
}

/**
 * Custom hook for managing partner requests
 * @param userId - Current user ID
 * @returns Partner requests state and handlers
 */
export function usePartnerRequests(userId?: string): UsePartnerRequestsReturn {
  const [isPausing, setIsPausing] = useState<string | null>(null);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);

  // Fetch and sort requests using useAsyncData
  const { data: requests, isLoading, refetch } = useAsyncData<PartnerRequest[]>(
    async () => {
      if (!userId) {
        return [];
      }
      const apiRequests = await getMyPartnerRequests(userId);
      // Map all requests and sort: active first, then by level
      const mappedRequests = apiRequests.map(mapApiRequestToPartnerRequest);
      return [...mappedRequests].sort(sortRequests);
    },
    [userId],
  );

  const handlePauseToggle = async (requestId: string) => {
    if (!userId || !requests) return;
    const request = requests.find((r) => r.id === requestId);
    if (!request) return;

    setIsPausing(requestId);
    const newIsActive = request.status === 'paused';
    const success = await togglePartnerRequestStatus(requestId, newIsActive);
    if (success) {
      toast.success(
        newIsActive ? 'Request activated successfully' : 'Request paused successfully',
      );
      // Refetch all requests to get updated list
      await refetch();
    }
    setIsPausing(null);
  };

  const handleDelete = async (requestId: string) => {
    if (!userId) return;
    const success = await deletePartnerRequest(requestId);
    if (success) {
      toast.success('Partner request deleted successfully');
      setRequestToDelete(null);
      // Refetch all requests to get updated list
      await refetch();
    }
  };

  return {
    requests: requests ?? [],
    isLoading,
    isPausing,
    requestToDelete,
    setRequestToDelete,
    handlePauseToggle,
    handleDelete,
  };
}

